<template>
  <div class="container-fluid JHeader JHeader--fixed">
    <div class="container">
      <header class="JHeader-content">
        <nav class="JNav" role="navigation" aria-label="Navegación principal">
          <!-- Menu cuando no hay sesión -->
          <ul v-if="!isAuthenticated" class="JNav_Menu">
            <li class="JNav_Menu-item">
              <router-link to="/login" class="JNav_Menu-item-btn login" aria-label="Iniciar sesión">
                <i class="fas fa-sign-in-alt" aria-hidden="true"></i>
                <span>Iniciar sesión</span>
              </router-link>
            </li>
          </ul>

          <!-- Menu cuando hay sesión activa -->
          <ul v-else class="JNav_Menu">
            <li class="JNav_Menu-item">
              <router-link :to="userDashboardPath" class="JNav_Menu-item-link" aria-label="Ir al panel de usuario" title="Mi perfil">
                <i class="fas fa-user" aria-hidden="true"></i>
                <span class="sr-only">Perfil</span>
              </router-link>
            </li>
            <li class="JNav_Menu-item">
              <button @click="handleLogout" class="JNav_Menu-item-link" aria-label="Cerrar sesión" title="Cerrar sesión">
                <i class="fas fa-sign-out-alt" aria-hidden="true"></i>
                <span class="sr-only">Cerrar sesión</span>
              </button>
            </li>
          </ul>
        </nav>
      </header>
    </div>
  </div>
</template>

<script>
import { UsersStorage } from "@/db/storage";
import { collapsed, toggleSidebar, sidebarWidth } from "../sidebar/state";
import UsuarioService from "@/services/usuario.service";
import authService from "@/services/auth.service";

export default {
  name: "HeaderPartial",

  data: () => ({
    users: null,
    slug: "",
    usersStorage: new UsersStorage(),
    loading: false,
  }),

  setup() {
    return { collapsed, toggleSidebar, sidebarWidth };
  },

  computed: {
    isAuthenticated() {
      return this.users !== null && localStorage.getItem("accessToken");
    },

    userDashboardPath() {
      if (!this.users) return "/";

      const { rol, company } = this.users;

      switch (rol) {
        case "user":
          return `/${this.slug || company?.slug || ""}/buzon`;
        case "admin":
        case "superadmin":
        case "manager":
        case "reader":
          return "/admin";
        default:
          return "/";
      }
    },
  },

  watch: {
    $route: {
      handler() {
        this.updateUserData();
      },
      immediate: true,
    },
  },

  mounted() {
    this.updateUserData();
    window.addEventListener("storage", this.handleStorageChange);
  },

  beforeUnmount() {
    window.removeEventListener("storage", this.handleStorageChange);
  },

  methods: {
    async updateUserData() {
      try {
        this.loading = true;

        // Obtener usuario desde el servicio (cache o API)
        const user = await authService.getCurrentUser();

        if (user) {
          this.users = user;

          // Actualizar slug
          const storedSlug = localStorage.getItem("slug");
          this.slug = storedSlug || this.$route.params.company || user.company?.slug || "";

          // Si hay company en el user pero no en slug, actualizarlo
          if (!this.slug && user.company?.slug) {
            this.slug = user.company.slug;
            localStorage.setItem("slug", this.slug);
          }
        } else {
          this.users = null;
          this.slug = "";
        }
      } catch (error) {
        console.error("Error al actualizar datos de usuario:", error);

        // Fallback a localStorage
        this.users = this.usersStorage.get();
        const storedSlug = localStorage.getItem("slug");
        this.slug = storedSlug || this.$route.params.company || "";
      } finally {
        this.loading = false;
      }
    },

    handleStorageChange(event) {
      // Actualizar cuando cambie el storage
      if (event.key === "slug" || event.key === "user" || event.key === "accessToken" || event.key === null) {
        this.updateUserData();
      }
    },

    async handleLogout() {
      await this.performLogout();
    },

    async performLogout() {
      try {
        // 1. Limpiar cache del servicio
        authService.clearCache();

        // 2. Notificar al backend para revocar el token
        try {
          await UsuarioService.logout();
          console.log("Token revocado en el servidor");
        } catch (logoutError) {
          console.warn("Error al revocar token en servidor:", logoutError);
          // Continuar con logout local incluso si falla el backend
        }

        // 3. Limpiar datos locales
        this.usersStorage.remove();
        localStorage.removeItem("accessToken");
        localStorage.removeItem("slug");
        localStorage.removeItem("user");
        localStorage.removeItem("company");

        // 4. Resetear estado local
        this.users = null;
        this.slug = "";

        // 5. Redirigir usando el router de Vue
        this.$router.push({ path: "/", replace: true });
      } catch (error) {
        console.error("Error al cerrar sesión:", error);
        // Fallback: limpiar y redirigir de todas formas
        authService.clearCache();
        localStorage.clear();
        window.location.href = "/";
      }
    },
  },
};
</script>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.JNav_Menu-item-link {
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: opacity 0.2s ease;
}

.JNav_Menu-item-link:hover {
  opacity: 0.8;
}

.JNav_Menu-item-link:focus {
  outline: 2px solid #355e91;
  outline-offset: 2px;
}

.JNav_Menu-item-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.JNav_Menu-item-btn:focus {
  outline: 2px solid #355e91;
  outline-offset: 2px;
}
</style>
